import type { Product } from "@decocms/apps-commerce/types"

/**
 * Sanitização dos HTMLs de produto da PDP, feita SÓ no servidor.
 *
 * O `sanitize-html` arrasta htmlparser2, dom-serializer, domutils e domhandler:
 * ~205 KB minificados que iam para o bundle do cliente de toda página só para
 * re-sanitizar, na hidratação, o HTML que o servidor já tinha entregue pronto.
 * Os loaders das sections (ProductDescription, ProductDetails) chamam estas
 * funções e passam as strings prontas como prop. O import é dinâmico porque os
 * arquivos de loader também entram no bundle do cliente.
 */
const loadSanitize = async () => (await import("sanitize-html")).default

const EXCLUDE_FROM_CHARACTERISTICS = ["Características", "CountDown", "Especificações"]

export interface SanitizedDescription {
  description: string
  characteristics: string
  otherSpecs: string
}

/** Os três blocos do `ProductDescription`, com as mesmas regras de antes. */
export async function sanitizeProductDescription(product: Product): Promise<SanitizedDescription> {
  const sanitize = await loadSanitize()
  const props = product.isVariantOf?.additionalProperty ?? []

  return {
    description: sanitize(product.description ?? product.isVariantOf?.description ?? ""),
    characteristics: sanitize(props.find(({ name }) => name === "Características")?.value ?? ""),
    otherSpecs: sanitize(
      props
        .filter(({ name }) => !EXCLUDE_FROM_CHARACTERISTICS.includes(name ?? ""))
        .map(({ name, value }) => `<span style='display: none;'><h4>${name}</h4>${value}</span>`)
        .join("") ?? "",
      {
        allowedTags: ["span", "h4", "p", "br", "strong", "em", "ul", "li", "ol", "b", "i"],
        allowedAttributes: {
          span: ["style"],
        },
        allowedStyles: {
          span: {
            display: [/^none$/],
          },
        },
      },
    ),
  }
}

/** Resumo de 350 caracteres do `InfosAndBuyButton`. */
export async function sanitizeShortDescription(description: string | undefined): Promise<string> {
  if (!description) return ""
  const sanitize = await loadSanitize()
  return sanitize(description.slice(0, 350))
}
